import { useCallback, useEffect, useRef, useState } from 'react';
import type { OverlayRect } from '@/utils/bboxToSourceRect';
import { clamp } from '@/utils/bboxToSourceRect';
import { findVideoInPlayerWrapper } from '@/utils/findVideoInPlayerWrapper';

const MIN_BOX_PX = 24;
const HANDLE_SIZE_PX = 14;
const POLL_MS = 120;

type DragKind = 'move' | 'resize-br' | 'resize-bl' | 'resize-tr' | 'resize-tl';

interface BboxSourceSelectionLayerProps {
    containerRef: React.RefObject<HTMLElement | null>;
    selectionVisible: boolean;
    onOverlayRectChange?: (rect: OverlayRect) => void;
    onVideoRectChange?: (rect: OverlayRect) => void;
}

export function BboxSourceSelectionLayer({
    containerRef,
    selectionVisible,
    onOverlayRectChange,
    onVideoRectChange,
}: BboxSourceSelectionLayerProps) {
    const [videoRect, setVideoRect] = useState<OverlayRect | null>(null);
    const [bboxRect, setBboxRect] = useState<OverlayRect | null>(null);
    const initializedRef = useRef(false);
    const videoRectRef = useRef<OverlayRect | null>(null);

    const measure = useCallback(() => {
        const container = containerRef.current;
        const video = findVideoInPlayerWrapper(container);
        if (!container || !video) {
            return;
        }
        const containerBounds = container.getBoundingClientRect();
        const videoBounds = video.getBoundingClientRect();
        const nextVideoRect: OverlayRect = {
            x: videoBounds.left - containerBounds.left,
            y: videoBounds.top - containerBounds.top,
            width: videoBounds.width,
            height: videoBounds.height,
        };
        videoRectRef.current = nextVideoRect;
        onVideoRectChange?.(nextVideoRect);
        setVideoRect((previous) => {
            const unchanged =
                previous &&
                previous.x === nextVideoRect.x &&
                previous.y === nextVideoRect.y &&
                previous.width === nextVideoRect.width &&
                previous.height === nextVideoRect.height;
            return unchanged ? previous : nextVideoRect;
        });
    }, [containerRef, onVideoRectChange]);

    useEffect(() => {
        measure();
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const resizeObserver = new ResizeObserver(() => measure());
        resizeObserver.observe(container);
        const mutationObserver = new MutationObserver(() => measure());
        mutationObserver.observe(container, {
            subtree: true,
            childList: true,
            attributes: true,
        });
        const pollId = window.setInterval(measure, POLL_MS);
        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            window.clearInterval(pollId);
        };
    }, [containerRef, measure]);

    useEffect(() => {
        if (!videoRect || initializedRef.current || videoRect.width < 8) {
            return;
        }
        initializedRef.current = true;
        const width = videoRect.width * 0.38;
        const height = videoRect.height * 0.38;
        const next: OverlayRect = {
            x: videoRect.x + (videoRect.width - width) / 2,
            y: videoRect.y + (videoRect.height - height) / 2,
            width,
            height,
        };
        setBboxRect(next);
        onOverlayRectChange?.(next);
    }, [onOverlayRectChange, videoRect]);

    useEffect(() => {
        if (!bboxRect) {
            return;
        }
        onOverlayRectChange?.(bboxRect);
    }, [bboxRect, onOverlayRectChange]);

    const constrainToVideo = useCallback(
        (draft: OverlayRect, bounds: OverlayRect): OverlayRect => {
            let { x, y, width, height } = draft;
            width = Math.max(MIN_BOX_PX, width);
            height = Math.max(MIN_BOX_PX, height);
            if (width > bounds.width) {
                width = bounds.width;
            }
            if (height > bounds.height) {
                height = bounds.height;
            }
            x = clamp(x, bounds.x, bounds.x + bounds.width - width);
            y = clamp(y, bounds.y, bounds.y + bounds.height - height);
            return { x, y, width, height };
        },
        [],
    );

    const attachDragListeners = useCallback(
        (
            kind: DragKind,
            originBbox: OverlayRect,
            startClientX: number,
            startClientY: number,
        ) => {
            const onMove = (event: PointerEvent) => {
                const bounds = videoRectRef.current;
                if (!bounds) {
                    return;
                }
                const dx = event.clientX - startClientX;
                const dy = event.clientY - startClientY;

                if (kind === 'move') {
                    const next = constrainToVideo(
                        {
                            x: originBbox.x + dx,
                            y: originBbox.y + dy,
                            width: originBbox.width,
                            height: originBbox.height,
                        },
                        bounds,
                    );
                    setBboxRect(next);
                    return;
                }

                if (kind === 'resize-br') {
                    const next = constrainToVideo(
                        {
                            x: originBbox.x,
                            y: originBbox.y,
                            width: originBbox.width + dx,
                            height: originBbox.height + dy,
                        },
                        bounds,
                    );
                    setBboxRect(next);
                    return;
                }

                if (kind === 'resize-tr') {
                    const next = constrainToVideo(
                        {
                            x: originBbox.x,
                            y: originBbox.y + dy,
                            width: originBbox.width + dx,
                            height: originBbox.height - dy,
                        },
                        bounds,
                    );
                    setBboxRect(next);
                    return;
                }

                if (kind === 'resize-bl') {
                    const next = constrainToVideo(
                        {
                            x: originBbox.x + dx,
                            y: originBbox.y,
                            width: originBbox.width - dx,
                            height: originBbox.height + dy,
                        },
                        bounds,
                    );
                    setBboxRect(next);
                    return;
                }

                const next = constrainToVideo(
                    {
                        x: originBbox.x + dx,
                        y: originBbox.y + dy,
                        width: originBbox.width - dx,
                        height: originBbox.height - dy,
                    },
                    bounds,
                );
                setBboxRect(next);
            };

            const onUp = () => {
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onUp);
                window.removeEventListener('pointercancel', onUp);
            };

            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
            window.addEventListener('pointercancel', onUp);
        },
        [constrainToVideo],
    );

    const startMove = (event: React.PointerEvent) => {
        if (!bboxRect) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        attachDragListeners('move', bboxRect, event.clientX, event.clientY);
    };

    const startResize =
        (kind: Exclude<DragKind, 'move'>) => (event: React.PointerEvent) => {
            if (!bboxRect) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            attachDragListeners(kind, bboxRect, event.clientX, event.clientY);
        };

    if (
        !selectionVisible ||
        !videoRect ||
        !bboxRect ||
        videoRect.width < 4 ||
        videoRect.height < 4
    ) {
        return null;
    }

    const innerBbox: OverlayRect = {
        x: bboxRect.x - videoRect.x,
        y: bboxRect.y - videoRect.y,
        width: bboxRect.width,
        height: bboxRect.height,
    };

    return (
        <div
            className="pointer-events-none absolute z-[28]"
            style={{
                left: videoRect.x,
                top: videoRect.y,
                width: videoRect.width,
                height: videoRect.height,
            }}
            aria-hidden
        >
            <div
                className="pointer-events-auto absolute border-2 border-brand"
                style={{
                    left: innerBbox.x,
                    top: innerBbox.y,
                    width: innerBbox.width,
                    height: innerBbox.height,
                    cursor: 'move',
                }}
                onPointerDown={startMove}
            >
                <div
                    className="pointer-events-auto absolute rounded-full border-2 border-black-100 bg-brand"
                    style={{
                        width: HANDLE_SIZE_PX,
                        height: HANDLE_SIZE_PX,
                        left: -HANDLE_SIZE_PX / 2,
                        top: -HANDLE_SIZE_PX / 2,
                        cursor: 'nwse-resize',
                    }}
                    onPointerDown={startResize('resize-tl')}
                />
                <div
                    className="pointer-events-auto absolute rounded-full border-2 border-black-100 bg-brand"
                    style={{
                        width: HANDLE_SIZE_PX,
                        height: HANDLE_SIZE_PX,
                        right: -HANDLE_SIZE_PX / 2,
                        top: -HANDLE_SIZE_PX / 2,
                        cursor: 'nesw-resize',
                    }}
                    onPointerDown={startResize('resize-tr')}
                />
                <div
                    className="pointer-events-auto absolute rounded-full border-2 border-black-100 bg-brand"
                    style={{
                        width: HANDLE_SIZE_PX,
                        height: HANDLE_SIZE_PX,
                        left: -HANDLE_SIZE_PX / 2,
                        bottom: -HANDLE_SIZE_PX / 2,
                        cursor: 'nesw-resize',
                    }}
                    onPointerDown={startResize('resize-bl')}
                />
                <div
                    className="pointer-events-auto absolute rounded-full border-2 border-black-100 bg-brand"
                    style={{
                        width: HANDLE_SIZE_PX,
                        height: HANDLE_SIZE_PX,
                        right: -HANDLE_SIZE_PX / 2,
                        bottom: -HANDLE_SIZE_PX / 2,
                        cursor: 'nwse-resize',
                    }}
                    onPointerDown={startResize('resize-br')}
                />
            </div>
        </div>
    );
}
